import AgoraRTM, { RtmChannel, RtmClient, RtmEvents } from 'agora-rtm-sdk';

import config from 'config';

import { ChannelMessage, PeerMessage } from 'types';
import { LoggerService } from 'services';

abstract class RTMService {
  static clientEvents: Array<keyof RtmEvents.RtmClientEvents> = [
    'ConnectionStateChanged',
    'MessageFromPeer',
    'PeersOnlineStatusChanged',
    'RemoteInvitationReceived',
    'TokenExpired'
  ];

  static channelEvents: Array<keyof RtmEvents.RtmChannelEvents> = [
    'AttributesUpdated',
    'ChannelMessage',
    'MemberCountUpdated',
    'MemberJoined',
    'MemberLeft'
  ];

  public static client: RtmClient;

  public static loggedIn: boolean;

  public static channels: {
    [channelName: string]: RtmChannel | null;
  };

  public static init() {
    // TODO: investigate deprecated flag issue
    if (!RTMService.client) {
      RTMService.client = AgoraRTM.createInstance(config.agora.appId);
    }
    RTMService.channels = {};
    RTMService.loggedIn = false;
  }

  private static subscribeClientEvents = () => {
    RTMService.clientEvents.forEach(eventName => {
      RTMService.client.on(eventName, (...args) => {
        console.log('emit client event', eventName, args);
      });
    });
  };

  private static subscribeChannelEvents(channelName: string) {
    if (!RTMService.channels[channelName]) throw Error('channel does not exist');

    RTMService.channelEvents.forEach(eventName => {
      RTMService.channels[channelName]?.on(eventName, (...args) => {
        console.log('emit channel event', eventName, args);
      });
    });
  }

  public static async login({ uid, token }: { uid: string; token?: string }): Promise<void> {
    if (RTMService.loggedIn) {
      await RTMService.logout();
    }

    await RTMService.client.login({ uid, token });

    RTMService.subscribeClientEvents();

    RTMService.loggedIn = true;
  }

  public static async logout(): Promise<void> {
    if (!RTMService.loggedIn) return;

    try {
      await RTMService.client.logout();
      RTMService.client.removeAllListeners();

      RTMService.loggedIn = false;
    } catch (error) {
      LoggerService.log('LOGOUT_FROM_AGORA_RTM_CHANNEL', error);
    }
  }

  public static async join(channelName: string): Promise<void> {
    if (RTMService.channels[channelName]) {
      await RTMService.leave(channelName);
    }

    const channel = RTMService.client.createChannel(channelName);

    RTMService.channels[channelName] = channel;
    RTMService.subscribeChannelEvents(channelName);
    await channel.join();
  }

  public static async leave(channelName: string): Promise<void> {
    try {
      const channel = RTMService.channels[channelName];

      if (!channel) throw Error('channel does not exist');

      RTMService.channels[channelName] = null;
      await channel.leave();
      channel.removeAllListeners();
    } catch (error) {
      LoggerService.log('LEAVE_AGORA_RTM_CHANNEL', error);
    }
  }

  public static async sendPeerMessage(peerId: string, message: PeerMessage): Promise<void> {
    await RTMService.client.sendMessageToPeer({ text: JSON.stringify(message) }, peerId);
  }

  public static sendChannelMessage = async (channelName: string, message: ChannelMessage): Promise<void> => {
    await RTMService.channels[channelName]?.sendMessage({ text: JSON.stringify(message) });
  };
}

export default RTMService;
